import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import GroupsIcon from "@mui/icons-material/Groups";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TuneIcon from "@mui/icons-material/Tune";
import { PitchIcon } from "../teams/formation/PitchIcon";
import { Avatar, Box, Button, Card, CardContent, CardHeader, Divider, Grid, Stack, Typography, useMediaQuery, useTheme, } from "@mui/material";
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRetrieveTeamFeed } from "../api/FeedApiProvider";
import { useListAggregatedPlayerStatsByTeam } from "../api/StatsApiProvider";
import { useListTeamHomeVenues } from "../api/TeamVenueApiProvider";
import { useClaimTeamOwnership, useDeleteTeam, useRetrievePlayerByUserId, useRetrievePlayersByTeamId, useRetrieveTeam, useRetrieveTeamPreferences, useRetrieveUserTeamPermissions, } from "../ApiProvider";
import { Feed } from "../feed/Feed";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { NotFound } from "../nav/NotFound";
import { PlayerListRow } from "../players/PlayerListRow";
import { StatsStack } from "../stats/StatsStack";
import { aggregatePlayerStats } from "../stats/utils";
import { AssistantManagersCard } from "../teams/AssistantManagersCard";
import { SquadCard } from "../teams/SquadCard";
import { TeamColorsCircle } from "../teams/TeamColors";
import { TransferRequestButton } from "../transfer/TransferRequestButton";
import { DeleteDialog } from "../utils/DeleteDialog";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { ResponsiveActionMenu, } from "../utils/ResponsiveActionMenu";
import { useNumericParams } from "../utils/useNumericParams";
import { VenuesCard } from "../venues/VenuesCard";
const Team = () => {
    const { id } = useNumericParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showDelete, setShowDelete] = useState(false);
    const [showClaimConfirm, setShowClaimConfirm] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const { data: userTeamPermissions, isLoading: userTeamPermissionsIsLoading, error: userTeamPermissionsError, } = useRetrieveUserTeamPermissions({ teamId: id });
    const { canModify = false, canView = false, canViewManager = false, canSubmitTransferRequest = false, canClaimOwnership = false, } = userTeamPermissions || {};
    const { isLoading: isTeamLoading, error, data: team, } = useRetrieveTeam({ id, enabled: canView });
    const { isLoading: isManagerLoading, error: managerError, data: manager, } = useRetrievePlayerByUserId({
        userId: team?.manager ? Number(team.manager) : undefined,
        enabled: canViewManager && !!team?.manager,
    });
    const { data: teamFeed, isLoading: teamFeedIsLoading, error: teamFeedError, fetchNextPage, hasNextPage, isFetchingNextPage, } = useRetrieveTeamFeed({
        team,
        limit: isMobile ? 5 : 10,
    });
    const { data: aggregatedStats, isLoading: aggregatedStatsIsLoading, error: aggregatedStatsError, } = useListAggregatedPlayerStatsByTeam({ teamId: id, enabled: canView });
    const filteredAggregatedStats = useMemo(() => {
        if (!aggregatedStats)
            return [];
        return aggregatePlayerStats(aggregatedStats, {
            years: [],
            types: [],
        });
    }, [aggregatedStats]);
    const { isLoading: teamPreferencesIsLoading, error: teamPreferencesError, data: teamPreference, } = useRetrieveTeamPreferences({ teamId: id, enabled: canView });
    const { isLoading: isTeamVenuesLoading, error: teamVenuesError, data: teamVenues, } = useListTeamHomeVenues({
        teamId: id,
        enabled: canView,
    });
    const { isLoading: playersIsLoading, error: playersError, data: players, } = useRetrievePlayersByTeamId({ teamId: id, enabled: canView });
    const mutation = useDeleteTeam({
        id,
        onMutate: () => {
            setDeleteLoading(true);
        },
        onSuccess: () => {
            navigate(`/teams/`);
        },
        onError: () => setDeleteLoading(false),
    });
    const claimMutation = useClaimTeamOwnership({
        id,
        onSuccess: () => setShowClaimConfirm(false),
        onError: () => setShowClaimConfirm(false),
    });
    const isLoading = (isTeamLoading && canView) ||
        isTeamVenuesLoading ||
        deleteLoading ||
        teamFeedIsLoading ||
        playersIsLoading ||
        teamPreferencesIsLoading ||
        aggregatedStatsIsLoading ||
        (isManagerLoading && canViewManager && !!team?.manager);
    const handleDelete = () => {
        mutation.mutate();
    };
    if (userTeamPermissionsIsLoading) {
        return _jsx(Loading, {});
    }
    if (userTeamPermissionsError) {
        return _jsx(ErrorView, { error: userTeamPermissionsError });
    }
    if (teamVenuesError) {
        return _jsx(ErrorView, { error: teamVenuesError });
    }
    if (!canView) {
        return _jsx(Forbidden, {});
    }
    if (isLoading) {
        return _jsx(Loading, {});
    }
    if (error ||
        playersError ||
        teamPreferencesError ||
        aggregatedStatsError ||
        players === undefined ||
        teamFeedError ||
        managerError ||
        !teamFeed ||
        !team ||
        !teamPreference) {
        return _jsx(ErrorView, { error: error });
    }
    if (teamVenues === undefined) {
        return _jsx(ErrorView, { error: "Failed to load team venues." });
    }
    if (id === undefined) {
        return _jsx(NotFound, {});
    }
    // Canonical order (shared with iOS): create → squad → manage → delete.
    // Managers get the full set; everyone can build a formation.
    const headerActions = canModify
        ? [
            {
                icon: _jsx(SportsSoccerIcon, {}),
                label: "Add Match",
                to: `/teams/${id}/external-match/create`,
            },
            {
                icon: _jsx(GroupsIcon, {}),
                label: "Add Intra-Squad Match",
                to: `/teams/${id}/intra-squad-match/create`,
            },
            {
                icon: _jsx(EmojiEventsIcon, {}),
                label: "Add Intra-Squad Tournament",
                to: `/teams/${id}/intra-squad-tournaments/create`,
            },
            {
                icon: _jsx(PersonAddAlt1Icon, {}),
                label: "Add Player",
                to: `/teams/${id}/add-player/`,
            },
            {
                icon: _jsx(PitchIcon, {}),
                label: "Build Formation",
                to: `/teams/${id}/formation/`,
            },
            {
                icon: _jsx(EditIcon, {}),
                label: "Edit Team",
                to: `/teams/${id}/edit/`,
                divider: true,
            },
            {
                icon: _jsx(TuneIcon, {}),
                label: "Preferences",
                to: `/teams/${team.id}/preferences/edit/`,
            },
            {
                icon: _jsx(MailOutlineIcon, {}),
                label: "Invitations",
                to: `/teams/${id}/invitations`,
            },
            {
                icon: _jsx(SwapHorizIcon, {}),
                label: "Transfer Ownership",
                to: `/teams/${id}/ownership-invitations`,
            },
            {
                icon: _jsx(DeleteIcon, {}),
                label: "Delete Team",
                onClick: () => setShowDelete(true),
                divider: true,
                destructive: true,
            },
        ]
        : [
            {
                icon: _jsx(PitchIcon, {}),
                label: "Build Formation",
                to: `/teams/${id}/formation/`,
            },
        ];
    const activitySection = (_jsxs(Box, { children: [_jsx(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", sx: { mb: 1 }, children: _jsx(Typography, { variant: "h6", children: "Latest Activity" }) }), _jsx(Feed, { feed: teamFeed, fetchNextPage: fetchNextPage, hasNextPage: hasNextPage, isFetchingNextPage: isFetchingNextPage })] }));
    const statsCard = _jsx(StatsStack, { stats: filteredAggregatedStats });
    const squadCard = (_jsx(SquadCard, { players: players, teamId: team.id, canModify: canModify }));
    const bioCard = team.bio ? (_jsx(Card, { children: _jsxs(CardContent, { children: [_jsx(Typography, { variant: "h6", children: "Bio" }), _jsx(Divider, { sx: { marginBottom: 2 } }), _jsx(Typography, { variant: "body1", children: team.bio })] }) })) : null;
    const managerCard = (_jsxs(_Fragment, { children: [canViewManager && manager && (_jsx(Card, { children: _jsxs(CardContent, { children: [_jsx(Typography, { variant: "h6", children: "Manager" }), _jsx(Divider, { sx: { marginBottom: 2 } }), _jsx(PlayerListRow, { player: manager })] }) })), team.manager === null && (_jsx(Card, { children: _jsxs(CardContent, { children: [_jsx(Typography, { variant: "h6", children: "Manager" }), _jsx(Divider, { sx: { marginBottom: 2 } }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: canClaimOwnership || canModify ? 2 : 0 }, children: "No manager assigned" }), _jsxs(Stack, { spacing: 1, children: [canClaimOwnership && (_jsx(Button, { variant: "outlined", size: "small", fullWidth: true, startIcon: _jsx(EditIcon, {}), onClick: () => setShowClaimConfirm(true), disabled: claimMutation.isLoading, children: claimMutation.isLoading ? "Claiming…" : "Claim as Manager" })), canModify && (_jsx(Link, { to: `/teams/${id}/ownership-invitations`, style: { textDecoration: "none" }, children: _jsx(Button, { variant: "outlined", size: "small", fullWidth: true, startIcon: _jsx(SwapHorizIcon, {}), children: "Invite a Manager" }) }))] })] }) }))] }));
    const assistantCard = (_jsx(AssistantManagersCard, { teamId: id, assistantManagerIds: team?.assistantManagers ?? [], canModify: canModify }));
    const venuesCard = (_jsx(VenuesCard, { venues: teamVenues.filter((tv) => tv.isActive).map((tv) => tv.venue), canModify: canModify, title: "Home Stadiums", manageUrl: `/teams/${id}/venues/` }));
    const colorsCard = (_jsxs(Card, { children: [_jsx(CardHeader, { title: _jsx(Typography, { variant: "h6", children: "Team Colors" }) }), _jsxs(CardContent, { children: [_jsx(Divider, { sx: { marginBottom: 2 } }), _jsxs(Stack, { spacing: 2, alignItems: "center", children: [_jsx(Box, { sx: {
                                    width: "100%",
                                    maxWidth: 150,
                                    aspectRatio: "1",
                                    borderRadius: "50%",
                                    border: "2px solid",
                                    borderColor: "divider",
                                    overflow: "hidden",
                                    boxShadow: 1,
                                }, children: _jsx(TeamColorsCircle, { color1: team.primaryColour, color2: team.secondaryColour }) }), _jsxs(Stack, { direction: "row", spacing: 4, justifyContent: "center", children: [_jsxs(Stack, { spacing: 0.5, alignItems: "center", children: [_jsx(Box, { sx: {
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: "4px",
                                                    bgcolor: team.primaryColour,
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                } }), _jsx(Typography, { variant: "caption", children: "Primary" })] }), _jsxs(Stack, { spacing: 0.5, alignItems: "center", children: [_jsx(Box, { sx: {
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: "4px",
                                                    bgcolor: team.secondaryColour,
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                } }), _jsx(Typography, { variant: "caption", children: "Secondary" })] })] })] })] })] }));
    return (_jsxs(React.Fragment, { children: [_jsx(ErrorSnackbar, { open: mutation.isError, message: "Failed to delete team. Please try again.", onClose: () => { } }), _jsx(ErrorSnackbar, { open: claimMutation.isError, message: "Failed to claim team ownership. Please try again.", onClose: () => { } }), _jsx(DeleteDialog, { open: showDelete, title: `Delete ${team.name}?`, onCancel: () => setShowDelete(false), onConfirm: handleDelete }), _jsx(DeleteDialog, { open: showClaimConfirm, title: "Claim Team Ownership?", titleColor: "primary.main", description: "As manager you'll control this team's roster, matches, and settings. Other members will see you listed as the manager.", confirmText: "Claim Ownership", confirmColor: "primary", onCancel: () => setShowClaimConfirm(false), onConfirm: () => claimMutation.mutate() }), _jsx(Card, { sx: { mb: 3 }, children: _jsx(CardHeader, { avatar: _jsx(Avatar, { sx: { width: 56, height: 56 }, src: team.logo || "", alt: team.name }), title: _jsx(Typography, { variant: "h5", component: "div", children: team.name }), subheader: _jsxs(Typography, { variant: "caption", fontStyle: "italic", children: [team.nickname, " EST \u2022 ", team.establishedYear] }), action: _jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, children: [canSubmitTransferRequest && (_jsx(TransferRequestButton, { team: team })), _jsx(ResponsiveActionMenu, { actions: headerActions, isMobile: isMobile, forceMenu: true })] }) }) }), isMobile ? (_jsxs(Stack, { spacing: 2, children: [activitySection, squadCard, bioCard, statsCard, managerCard, assistantCard, venuesCard, colorsCard] })) : (
            // Desktop: unchanged 3-column layout.
            _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, md: 3, children: statsCard }), _jsx(Grid, { item: true, md: 6, children: activitySection }), _jsx(Grid, { item: true, md: 3, children: _jsxs(Stack, { spacing: 2, children: [squadCard, bioCard, managerCard, assistantCard, venuesCard, colorsCard] }) })] }))] }));
};
export default Team;
